import { useState, useEffect, useRef, useCallback } from "react";
import { TableLoader } from "../../../components/LoaderC";
import styles from "./leases.module.css";
import LeaseCard from "./component/LeaseCard";
import LeaseFilter from "../components/Filter";
import { TablePagination } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";

const isActiveFilter = (value) => value !== "" && value !== "all" && value !== undefined && value !== null;

const LeaseGridView = ({ handleView, filterData, setFilterData, isGridView }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [gridData, setGridData] = useState([]);
    const [total, setTotal] = useState(0);
    const [perPage, setPerPage] = useState(8);
    const [page, setPage] = useState(0);
    const [contentLoading, setContentLoading] = useState(true);
    const [refetching, setRefetching] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const [debouncedSearch, setDebouncedSearch] = useState(filterData?.searchKey || "");

    const requestSeq = useRef(0);
    const hasLoadedOnceRef = useRef(false);
    const lastFetchedKeyRef = useRef(null);

    const property = filterData?.property;
    const apartment = filterData?.apartment;
    const status = filterData?.status;

    const filterKey = JSON.stringify([debouncedSearch, property, apartment, status]);
    const fetchKey = JSON.stringify([filterKey, page, perPage]);
    const lastFilterKeyRef = useRef(filterKey);

    const fetchLeases = useCallback(async () => {
        const seq = ++requestSeq.current;
        const requestKey = fetchKey;
        const isInitialLoad = !hasLoadedOnceRef.current;

        if (isInitialLoad) {
            setContentLoading(true);
        } else {
            setRefetching(true);
        }

        let params = { page: page + 1, per_page: perPage === -1 ? "all" : perPage };
        if (debouncedSearch.trim() !== "") params.search = debouncedSearch.trim();
        if (isActiveFilter(property)) params.property_id = property;
        if (isActiveFilter(apartment)) params.apt_id = apartment;
        if (isActiveFilter(status)) params.status = status;

        try {
            const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

            const response = await axios(process.env.REACT_APP_PAYMENT_API_URL + "v1/leases-grid", {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: params,
            });

            if (seq !== requestSeq.current) return;

            setGridData(response.data?.data || []);
            setTotal(response.data?.meta?.total || 0);
            setLoadError(false);
            lastFetchedKeyRef.current = requestKey;
            hasLoadedOnceRef.current = true;
        } catch (error) {
            if (seq !== requestSeq.current) return;
            console.error("[Leases] Failed to load lease grid", error);
            toast.error(error?.response?.data?.message || "Unable to load leases. Please try again.");
            setLoadError(true);
            setGridData([]);
            setTotal(0);
        } finally {
            if (seq === requestSeq.current) {
                if (isInitialLoad) setContentLoading(false);
                else setRefetching(false);
            }
        }
    }, [getAccessTokenSilently, fetchKey, page, perPage, debouncedSearch, property, apartment, status]);

    // Debounce search text input
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(filterData?.searchKey || "");
        }, 400);

        return () => clearTimeout(handler);
    }, [filterData?.searchKey]);

    useEffect(() => {
        // Search/filter change: go back to the first page (that page change triggers the fetch)
        if (lastFilterKeyRef.current !== filterKey) {
            lastFilterKeyRef.current = filterKey;
            if (page !== 0) {
                setPage(0);
                return;
            }
        }

        // Only load while the grid is visible, and skip if this exact page is already loaded
        if (!isGridView || lastFetchedKeyRef.current === fetchKey) return;

        fetchLeases();
    }, [isGridView, filterKey, fetchKey, page, fetchLeases]);

    return (<div className={`${styles.BodyCon}`}>
        <div className={`${styles.mainCardDiv}`}>
            <div className={`${styles.mainCard}`}>
                <div className="control-pane">

                    <LeaseFilter isGridView={isGridView} handleView={handleView.bind(this)} filterData={filterData} setFilterData={setFilterData.bind(this)} setPage={setPage} />

                    {contentLoading && (<div className="Loader TableLoader"><TableLoader /></div>)}

                    {!contentLoading && (<div className={refetching ? `${styles.NotesRow} ${styles.Refetching}` : `${styles.NotesRow}`}>

                        {gridData.map((item) => {
                            return (<LeaseCard key={item?.list_id || item?.id} item={item} />);
                        })}

                        {total > 0 && (<div className="Pagination TablePaginationNew List">
                            <TablePagination
                                count={total}
                                color="primary"
                                page={page}
                                rowsPerPage={perPage}
                                onPageChange={(e, v) => setPage(v)}
                                rowsPerPageOptions={[
                                    { label: '4', value: 4 },
                                    { label: '8', value: 8 },
                                    { label: '16', value: 16 },
                                    { label: 'All', value: -1 },
                                ]}
                                onRowsPerPageChange={(e) => {
                                    setPage(0);
                                    setPerPage(e.target.value);
                                }}
                                labelRowsPerPage={'Leases per Page:'}
                            />
                        </div>)}

                        {loadError && (<div className={`${styles.NoDataMain}`}>
                            <p className={`${styles.NoDataText}`}>Something went wrong!</p>
                            <p className={`${styles.NoDataTextSub}`}>we couldn't load the leases.</p>
                            <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchLeases()}>Retry</button>
                        </div>)}

                        {!loadError && total === 0 && (<div className={`${styles.NoDataMain}`}>
                            <div className={`${styles.NoDataIMG}`}>
                                <img src="/static/img/no-lease.png" alt="No Data" />
                            </div>
                            <p className={`${styles.NoDataText}`}>Unfortunately!</p>
                            <p className={`${styles.NoDataTextSub}`}>we couldn't find any leases at this time.</p>
                        </div>)}

                    </div>)}

                </div>
            </div>
        </div>
    </div >)
}

export default LeaseGridView;
